import { Flex, Text, useBreakpointValue, useOutsideClick, useDisclosure, useColorMode } from '@chakra-ui/react';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';

// UI Components
import DynamicDataTable from '../../masters/components/DynamicDataTable';
import { MdNavigateNext, MdNavigateBefore } from 'react-icons/md';
import { SearchIcon } from '@chakra-ui/icons';
import { IoFilterOutline } from 'react-icons/io5';
import { IoMdArrowRoundBack } from 'react-icons/io';

// Utils
import { deleteRecord, deleteRecordForSales } from '../../../../utils/util';

// Actions
import {
   getPurchaseInvoices,
   deletePurchaseInvoice,
   emptyPurchase,
   getSinglePurchaseWithPrevAndNext,
   updateSyncPurchaseInvoice,
   deleteSyncCode,
} from '../../../../redux/actions/purchaseInvoice';

import { useDispatch } from 'react-redux';
import { useUser, useGlobal } from 'contexts';
import { useDaybook } from '../../../../contexts/DaybookContext';
import SalesInvoiceFilterModal from 'views/admin/sales/Pages/SalesInvoiceMaster/SalesInvoiceFilterModal';
import SalesDetails from 'components/salesDetails/SalesDetails';
import TransactionDetailModal from 'views/admin/masters/components/TransactionDetailModel';
import { getSingleWithPrevAndNext } from '../../../../redux/actions/salesInvoice';
import Loader from 'views/admin/default/Loader';
import DeletePasswordModal from 'views/admin/sales/Pages/SalesInvoiceMaster/DeletePasswordModal';
import { RefreshCcw } from 'lucide-react';

const columnHelper = createColumnHelper();
const MotionFlex = motion(Flex);
const MotionSearchIcon = motion(SearchIcon);

const UnusedPurchaseInvoiceMaster = () => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const navigate = useNavigate();
   const dispatch = useDispatch();
   const { user } = useUser();
   const { userGlobalSettingsData } = useGlobal();
   const daybooks = useSelector((state) => state?.daybook?.list);

   // Disclosure hooks
   const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();
   const { isOpen, onOpen, onClose } = useDisclosure();
   const {
      isOpen: isBillToBillModal,
      onOpen: onOpenBillToBillModal,
      onClose: onCloseBillToBillModal,
   } = useDisclosure();

   // Redux state
   const purchaseInvoice = useSelector((state) => state.purchaseInvoice?.list || []);
   const Totals = useSelector((state) => state.purchaseInvoice.pagination.totals);
   const pagination = useSelector((state) => state.purchaseInvoice?.pagination);
   const totalCount = useSelector((state) => state.purchaseInvoice?.totalCount);
   // const viewData = useSelector((state) => state?.purchaseInvoice?.purchaseData);
   const isLoading = useSelector((state) => state.purchaseInvoice?.loading);

   // Local state
   const [currentPage, setCurrentPage] = useState(1);
   const [limit, setLimit] = useState(10);
   const [data, setData] = useState([]);
   const [isEditingPage, setIsEditingPage] = useState(false);
   const [pageInputValue, setPageInputValue] = useState('');
   const [searchQuery, setSearchQuery] = useState('');
   const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
   const [expanded, setExpanded] = useState(false);
   const [paginateData, setPaginateData] = useState({});
   const [accountId, setAccountId] = useState(null);
   const [companyId, setCompanyId] = useState(null);
   const [invoiceData, setInvoiceData] = useState(null);
   const [sorting, setSorting] = useState([]);
   const [isEditFormOpen, setIsEditFormOpen] = React.useState(false);
   const [isModalLoading, setIsModalLoading] = useState(false);
   const [singleEntity, setSingleEntity] = useState(null);
   const viewData = singleEntity;
   const [isOpenSingleView, setIsOpenSingleView] = useState(false);
   const [dataId, setDataId] = useState(null);
   const [isModalOpen, setIsModalOpen] = useState(false);

   // Add a state to track if data has been loaded
   const [hasLoaded, setHasLoaded] = useState(false);

   const purchaseInvoiceRight = Array.isArray(user?.userRights)
      ? user.userRights.find((right) => right.companyId === user?.loginhistory?.companyId)?.purchaseinvoice
      : null;

   const { viewDetail, paginate } = useMemo(() => {
      const data = viewData;
      const pagination = viewData?.pagination;
      return { viewDetail: data, paginate: pagination };
   }, [viewData]);

   const [columnVisibility] = useState({
      docno: true,
      transactionAt: true,
      itemname: true,
      serialno: true,
      daybookname: true,
      accountname: true,
      qty1: false,
      qty2: false,
      qty3: false,
      taxableAmount: true,
      gstAmount: true,
      netAmount: true,
      pendingamt: true,
      pendingstatus: true,
      createdu: false,
      updatedu: false,
      status: false,
      action: true,
   });

   const flattenedData = useMemo(() => {
      if (!data || !Array.isArray(data)) return [];

      const flatData = [];

      data.forEach((invoice) => {
         // Calculate totals for quantities only (if needed)
         let totalQty1 = 0;
         let totalQty2 = 0;
         let totalQty3 = 0;

         const allSerialNos = [];
         const allItemNames = [];

         if (invoice.lineitems && Array.isArray(invoice.lineitems) && invoice.lineitems.length > 0) {
            invoice.lineitems.forEach((lineItem) => {
               totalQty1 += Number(lineItem.qty1) || 0;
               totalQty2 += Number(lineItem.qty2) || 0;
               totalQty3 += Number(lineItem.qty3) || 0;

               // Collect serial numbers
               if (lineItem.salesLineDetail) {
                  lineItem.salesLineDetail.forEach((detail) => {
                     if (detail.serialNo) {
                        allSerialNos.push(detail.serialNo);
                     }
                  });
               }

               // Collect item names
               if (lineItem.itemName) {
                  allItemNames.push(lineItem.itemName);
               }
            });
         }

         flatData.push({
            ...invoice,
            qty1: totalQty1,
            qty2: totalQty2,
            qty3: totalQty3,
            taxableAmount: invoice.taxableAmount,
            gstAmount: invoice.gstAmount,
            netAmount: invoice.netAmount,
            pendingamt: invoice.pendingamt,

            itemname: allItemNames.length > 0 ? allItemNames.join(', ') : 'No Items',
            serialno: allSerialNos.length > 0 ? allSerialNos.join(', ') : '',

            _aggregatedTotals: {
               qty1: totalQty1,
               qty2: totalQty2,
               qty3: totalQty3,
               itemCount: invoice.lineitems?.length || 0,
            },
         });
      });
      return flatData;
   }, [data]);
   const quantityTotals = useMemo(() => {
      let totalQty1 = 0;
      let totalQty2 = 0;
      let totalQty3 = 0;

      if (flattenedData && flattenedData.length) {
         flattenedData.forEach((invoice, invIdx) => {
            totalQty1 += Number(invoice.qty1) || 0;
            totalQty2 += Number(invoice.qty2) || 0;
            totalQty3 += Number(invoice.qty3) || 0;
         });
      }

      return { totalQty1, totalQty2, totalQty3 };
   }, [flattenedData]);

   const columnsData = useMemo(
      () => [
         { id: 'docno', header: 'DOC NO', accessor: 'docno' },
         { id: 'transactionAt', header: 'DOC Date', accessor: 'transactionAt' },
         { id: 'partyNo', header: 'Bill No', accessor: 'partyNo' },
         { id: 'partyDate', header: 'Bill Date', accessor: 'partyDate' },
         {
            id: 'itemname',
            header: 'Item',
            accessor: 'itemname',
         },
         {
            id: 'serialno',
            header: 'serial No',
            accessor: 'serialno',
         },
         { id: 'daybookname', header: 'Daybook', accessor: 'daybookname' },
         { id: 'accountname', header: 'Party', accessor: 'accountname' },
         {
            id: 'qty1',
            header: `Qty 1 (${quantityTotals.totalQty1})`,
            accessor: 'qty1',
            Cell: ({ value }) => {
               return value;
            },
         },
         {
            id: 'qty2',
            header: `Qty 2 (${quantityTotals.totalQty2})`,
            accessor: 'qty2',
            Cell: ({ value }) => {
               return value;
            },
         },
         {
            id: 'qty3',
            header: `Qty 3 (${quantityTotals.totalQty3})`,
            accessor: 'qty3',
            Cell: ({ value }) => {
               return value;
            },
         },
         {
            id: 'taxableAmount',
            header: (
               <div>
                  <Text>Taxable Amount</Text>
                  {Totals?.totalTaxableAmount > 0 && (
                     <Text fontSize="xs" color="gray.500">
                        (₹ {Totals.totalTaxableAmount.toFixed(2)})
                     </Text>
                  )}
               </div>
            ),
            accessor: 'taxableAmount',
         },
         { id: 'gstAmount', header: 'GST', accessor: 'gstAmount' },
         {
            id: 'netAmount',
            header: (
               <div>
                  <Text>Net Amount</Text>
                  {Totals?.totalNetAmount > 0 && (
                     <Text fontSize="xs" color="gray.500">
                        (₹ {Totals.totalNetAmount.toFixed(2)})
                     </Text>
                  )}
               </div>
            ),
            accessor: 'netAmount',
         },
         {
            id: 'pendingamt',
            header: (
               <div>
                  <Text>Pending Amount</Text>
                  {Totals?.totalPendingAmount > 0 && (
                     <Text fontSize="xs" color="gray.500">
                        (₹ {Totals.totalPendingAmount.toFixed(2)})
                     </Text>
                  )}
               </div>
            ),
            accessor: 'pendingamt',
         },
         { id: 'createdu', header: 'Created By', accessor: 'createdu' },
         { id: 'updatedu', header: 'Updated By', accessor: 'updatedu' },
         { id: 'status', header: 'STATUS', accessor: 'status' },
      ],
      [Totals, quantityTotals],
   );

   const [filters, setFilters] = useState({
      pendingstatus: 'all',
      invoiceBook: 'all',
      duration: 'any',
      contact: '',
      item: '',
      daybookId: '',
      daybookIds: '',
      accountIds: '',
      accountId: '',
      fromDate: '',
      toDate: '',
   });

   const handleApplyFilters = (newFilters) => {
      setFilters(newFilters);
      setCurrentPage(1);
   };

   const columns = columnsData
      .filter((column) => columnVisibility[column.id])
      ?.map((column) =>
         columnHelper.accessor(column.id, {
            id: column.id,
            header: () => (
               <Text justifyContent="space-between" align="center" fontSize={{ sm: '10px', lg: '12px' }}>
                  {column.header}
               </Text>
            ),
            cell: (info) => <Flex align="center">{info.getValue()}</Flex>,
         }),
      );

   const table = useReactTable({
      data,
      columns,
      state: { sorting },
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
   });

   const wrapperRef = useRef();

   const handlePageChange = useCallback(
      (directionOrPage) => {
         if (typeof directionOrPage === 'number') {
            setCurrentPage(directionOrPage);
         } else {
            setCurrentPage((prevPage) => {
               if (directionOrPage === 'next') {
                  return prevPage < pagination?.totalPages ? prevPage + 1 : prevPage;
               } else {
                  return prevPage > 1 ? prevPage - 1 : prevPage;
               }
            });
         }
      },
      [pagination?.totalPages],
   );

   const EditSyncRecord = (id) => {
      const formdata = { id, status: true };
      const sortBy = sorting.length > 0 ? sorting[0]?.id : 'id';
      const sortOrder = sorting.length > 0 ? (sorting[0]?.desc ? 'DESC' : 'ASC') : 'DESC';

      dispatch(updateSyncPurchaseInvoice(formdata)).then(() => {
         dispatch(
            getPurchaseInvoices({
               page: currentPage,
               limit: limit,
               search: debouncedSearchQuery,
               companyId: user?.loginhistory?.companyId,
               sortBy,
               sortOrder,
               paymentStatus: filters.paymentStatus !== 'all' ? filters.paymentStatus : undefined,
               invoiceBook: filters.invoiceBook !== 'all' ? filters.invoiceBook : undefined,
               duration: filters.duration !== 'any' ? filters.duration : undefined,
               contact: filters.contact || undefined,
               item: filters.item || undefined,
               daybookId: filters.daybookId || undefined,
               daybookIds: filters.daybookIds || undefined,
               accountIds: filters.accountIds || undefined,
               accountId: filters.accountId || undefined,
               fromDate: filters.fromDate || undefined,
               toDate: filters.toDate || undefined,
               status: false,
               isSync: true,
            }),
         );
      });
   };

   const handlePageClick = () => {
      setIsEditingPage(true);
      setPageInputValue('');
   };

   const handlePageInputSubmit = () => {
      const page = parseInt(pageInputValue);
      if (!isNaN(page) && page >= 1 && page <= pagination?.totalPages) {
         handlePageChange(page);
      }
      setIsEditingPage(false);
      setPageInputValue('');
   };

   const handlePageKeyDown = (e) => {
      if (e.key === 'Enter') {
         handlePageInputSubmit();
      } else if (e.key === 'Escape') {
         setIsEditingPage(false);
         setPageInputValue('');
      }
   };

   const deletePurchase = () => {
      deleteRecord(viewDetail?.id, deleteSyncCode, dispatch, user?.loginhistory?.companyId, userGlobalSettingsData);
      onClose();
   };
   const handleViewSingleData = async (data, action = 'new') => {
      setIsModalLoading(true); // Use separate state
      onOpen(); // Open modal immediately

      try {
         if (action === 'update') {
            const res = await dispatch(
               getSingleWithPrevAndNext({
                  id: data.id,
                  page: 1,
                  limit: 1,
                  sortBy: 'id',
                  sortOrder: 'desc',
               }),
            );
            setSingleEntity(res?.[0]);
         } else {
            setSingleEntity(data);
         }
         // await dispatch(getSaleInvoice(id));
         const currentIdx = purchaseInvoice.findIndex((x) => x.id === data.id);

         const prevObject = currentIdx > 0 ? purchaseInvoice[currentIdx - 1] : null;
         const nextObject = currentIdx < purchaseInvoice.length - 1 ? purchaseInvoice[currentIdx + 1] : null;

         // await dispatch(
         //    getSingleWithPrevAndNext({
         //       id: id,
         //       page: 1,
         //       limit: 1,
         //       sortBy: 'id',
         //       sortOrder: 'desc',
         //    }),
         // );
         setPaginateData({
            hasPreviousPage: prevObject ? true : false,
            previousPage: prevObject ? currentIdx - 1 : null,
            nextPage: nextObject ? currentIdx + 1 : null,
            hasNextPage: nextObject ? true : false,
         });
         setIsOpenSingleView(true);
      } catch (error) {
         console.error('Error fetching sales data:', error);
      } finally {
         setIsModalLoading(false); // Update separate state
      }
   };

   const handlePrevPage = useCallback(
      async (page) => {
         await dispatch(
            getSinglePurchaseWithPrevAndNext({
               page: page,
               limit: 1,
               sortBy: 'id',
               sortOrder: 'desc',
            }),
         );
      },
      [dispatch],
   );

   const handleNextPage = useCallback(
      async (page) => {
         await dispatch(
            getSinglePurchaseWithPrevAndNext({
               page: page,
               limit: 1,
               sortBy: 'id',
               sortOrder: 'desc',
            }),
         );
      },
      [dispatch],
   );

   const handleOnClose = useCallback(() => {
      dispatch(emptyPurchase());
      onClose(true);
   }, [dispatch, onClose]);

   const handleOnCloseBillToBill = useCallback(() => {
      setAccountId(null);
      setCompanyId(null);
      onCloseBillToBillModal();
   }, [onCloseBillToBillModal]);

   const getPurchase = useCallback(async () => {
      if (user && user?.loginhistory?.companyId) {
         const sortBy = sorting.length > 0 ? sorting[0]?.id : 'id';
         const sortOrder = sorting.length > 0 ? (sorting[0]?.desc ? 'DESC' : 'ASC') : 'DESC';

         await dispatch(
            getPurchaseInvoices({
               page: currentPage,
               limit: limit,
               search: debouncedSearchQuery,
               companyId: user?.loginhistory?.companyId,
               sortBy,
               sortOrder,
               paymentStatus: filters.paymentStatus !== 'all' ? filters.paymentStatus : undefined,
               invoiceBook: filters.invoiceBook !== 'all' ? filters.invoiceBook : undefined,
               duration: filters.duration !== 'any' ? filters.duration : undefined,
               contact: filters.contact || undefined,
               item: filters.item || undefined,
               daybookId: filters.daybookId || undefined,
               daybookIds: filters.daybookIds || undefined,
               accountIds: filters.accountIds || undefined,
               accountId: filters.accountId || undefined,
               fromDate: filters.fromDate || undefined,
               toDate: filters.toDate || undefined,
               status: false,
               isSync: true,
            }),
         );
         // Mark that data has been loaded
         setHasLoaded(true);
      }
   }, [currentPage, dispatch, filters, limit, debouncedSearchQuery, sorting, user]);

   useOutsideClick({
      ref: wrapperRef,
      handler: () => {
         if (debouncedSearchQuery === '') setExpanded(false);
      },
   });

   useEffect(() => {
      getPurchase();
   }, [getPurchase]);

   useEffect(() => {
      setPaginateData(paginate);
   }, [paginate]);

   useEffect(() => {
      setData(purchaseInvoice);
   }, [purchaseInvoice]);

   const debounce = (func, delay) => {
      let timer;
      return function (...args) {
         clearTimeout(timer);
         timer = setTimeout(() => func.apply(this, args), delay);
      };
   };

   const debouncedSearchHandler = useCallback(
      debounce((query) => {
         setDebouncedSearchQuery(query);
      }, 500),
      [],
   );

   const handleSearchChange = (e) => {
      const value = e.target.value;
      setSearchQuery(value);
      debouncedSearchHandler(value);
   };

   const isMobileOrTablet = useBreakpointValue({ base: true, md: false, lg: false });

   // Check if we should show empty state
   const showEmptyState = hasLoaded && !isLoading && data.length === 0;
   // Check if we should show loading state
   const showLoading = isLoading || !hasLoaded;

   return (
      <>
         <div
            className={`w-full  px-0 overflow-x-auto mt-7 relative py-2.5 ${isDark ? 'bg-gray-900' : 'bg-gray-50'
               } transition-colors duration-200`}>
            <div className="flex flex-col w-full">
               {/* Header Section */}
               <div
                  className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                     } border-b shadow-sm mb-6 mt-5`}>
                  <div
                     className={`flex ${isMobileOrTablet ? 'flex-col' : 'flex-row'
                        } justify-between items-center px-6 py-4 gap-4`}>
                     {/* Left Section - Title and Back Button */}
                     <div className="flex items-center gap-3">
                        <Link to="/admin/utility">
                           <button
                              className={`p-2 rounded-lg ${isDark
                                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                 } transition-colors`}>
                              <IoMdArrowRoundBack size={20} />
                           </button>
                        </Link>
                        <div>
                           <h1 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              Synced Un-Approved Entry
                           </h1>
                        </div>
                     </div>

                     {/* Right Section - Controls */}
                     <div className="flex items-center gap-3 flex-wrap">
                        <button
                           onClick={getPurchase}
                           className={`p-2 rounded-lg ${isDark
                                 ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                                 : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              } transition-colors`}>
                           <RefreshCcw size={20} />
                        </button>
                        {/* Filter Button */}
                        <button
                           onClick={onFilterOpen}
                           className={`p-2 rounded-lg ${isDark
                                 ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                                 : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              } transition-colors`}>
                           <IoFilterOutline size={20} />
                        </button>

                        {/* Pagination Controls */}
                        <div
                           className={`flex items-center gap-2 px-3 py-1 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'
                              }`}>
                           <button
                              onClick={() => handlePageChange('prev')}
                              disabled={currentPage <= 1}
                              className={`p-1 rounded transition-colors ${currentPage > 1
                                    ? `${isDark ? 'hover:bg-gray-600 text-gray-200' : 'hover:bg-gray-200 text-gray-700'
                                    } cursor-pointer`
                                    : 'cursor-not-allowed opacity-40'
                                 }`}>
                              <MdNavigateBefore size={20} />
                           </button>

                           <div className="flex items-center">
                              {!isEditingPage ? (
                                 <button
                                    onClick={handlePageClick}
                                    className={`min-w-[40px] text-center px-2 py-1 rounded font-medium ${isDark
                                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                                          : 'bg-blue-500 text-white hover:bg-blue-600'
                                       } transition-colors`}>
                                    {currentPage}
                                 </button>
                              ) : (
                                 <input
                                    type="number"
                                    min={1}
                                    max={pagination?.totalPages || 1}
                                    value={pageInputValue}
                                    onChange={(e) => setPageInputValue(e.target.value)}
                                    onBlur={handlePageInputSubmit}
                                    onKeyDown={handlePageKeyDown}
                                    className={`w-14 text-center border rounded px-2 py-1 ${isDark
                                          ? 'bg-gray-600 border-gray-500 text-white'
                                          : 'bg-white border-gray-300 text-gray-900'
                                       }`}
                                    autoFocus
                                 />
                              )}
                              <span className={`mx-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                 / {pagination?.totalPages || 1}
                              </span>
                           </div>

                           <button
                              onClick={() => handlePageChange('next')}
                              disabled={currentPage >= (pagination?.totalPages || 1)}
                              className={`p-1 rounded transition-colors ${currentPage < (pagination?.totalPages || 1)
                                    ? `${isDark ? 'hover:bg-gray-600 text-gray-200' : 'hover:bg-gray-200 text-gray-700'
                                    } cursor-pointer`
                                    : 'cursor-not-allowed opacity-40'
                                 }`}>
                              <MdNavigateNext size={20} />
                           </button>
                        </div>

                        {/* Page Size Select */}
                        <select
                           value={limit === totalCount ? 'ALL' : limit.toString()}
                           onChange={(e) => {
                              const newLimit = e.target.value === 'ALL' ? totalCount : Number(e.target.value);
                              setLimit(newLimit);
                              setCurrentPage(1);
                           }}
                           className={`px-3 py-2 rounded-lg border font-medium ${isDark
                                 ? 'bg-gray-700 border-gray-600 text-white'
                                 : 'bg-white border-gray-300 text-gray-900'
                              } focus:outline-none focus:ring-2 focus:ring-blue-500`}>
                           <option value="10">10</option>
                           <option value="20">20</option>
                           <option value="30">30</option>
                           <option value="ALL">All</option>
                        </select>

                        {/* Search Field */}
                        <MotionFlex
                           ref={wrapperRef}
                           align="center"
                           className={`rounded-lg cursor-pointer px-3 h-10 transition-all ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                              } ${expanded ? 'w-64' : 'w-10'} overflow-hidden`}
                           onClick={() => setExpanded(true)}>
                           <MotionSearchIcon
                              className={isDark ? 'text-gray-300' : 'text-gray-600'}
                              animate={{ rotate: expanded ? 0 : 90 }}
                              transition={{ duration: 0.2 }}
                           />
                           {expanded && (
                              <input
                                 type="text"
                                 placeholder="Search..."
                                 value={searchQuery}
                                 onChange={handleSearchChange}
                                 className={`bg-transparent border-none outline-none w-full ml-2 ${isDark ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'
                                    }`}
                                 autoFocus
                              />
                           )}
                        </MotionFlex>
                     </div>
                  </div>
               </div>

               {/* Data Table Container */}
               <div className="px-6 mb-20">
                  {showLoading ? (
                     // Show loader when loading
                     <div
                        className={`rounded-lg shadow overflow-hidden border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                           } flex items-center justify-center h-64`}>
                        <div className="text-center">
                           <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                           <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>Loading data...</p>
                        </div>
                     </div>
                  ) : showEmptyState ? (
                     // Show empty state when no data
                     <div
                        className={`rounded-lg shadow overflow-hidden border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                           } flex items-center justify-center h-64`}>
                        <div className="text-center">
                           <div className={`text-5xl mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>📄</div>
                           <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                              No Data Found
                           </h3>
                           <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                              No un-approved purchase invoices available.
                           </p>
                        </div>
                     </div>
                  ) : (
                     // Show table when there's data
                     <div
                        className={`rounded-lg shadow overflow-hidden border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                           }`}>
                        <DynamicDataTable
                           singleEntityRights={purchaseInvoiceRight}
                           table={table}
                           tableData={flattenedData}
                           columnsData={columns}
                           emptyMessage="No purchase invoice data found"
                           isLoading={false} // Set to false since we're handling loading state above
                           handleViewSingleData={handleViewSingleData}
                           onEditClick={(row) => EditSyncRecord(row.id)}
                           onSyncAccept={(id) => EditSyncRecord(id)}
                           onDeleteClick={(id) => {
                              const purchase = flattenedData.find((item) => item.id === id);
                              const daybook = daybooks.find((item) => item.id === purchase.daybookId);
                              if (daybook && daybook?.askPasswordOnDelete) {
                                 setDataId({
                                    id: id,
                                    daybookId: purchase?.daybookId,
                                    accountId: purchase?.accountId,
                                 });
                                 setIsModalOpen(true);
                              } else {
                                 deleteRecord(
                                    id,
                                    deleteSyncCode,
                                    dispatch,
                                    user?.loginhistory?.companyId,
                                    userGlobalSettingsData,
                                 );
                              }
                           }}
                           onBillToBillClick={(row) => {
                              setAccountId(row.accountId);
                              setCompanyId(row.companyId);
                              setInvoiceData(row);
                              onOpenBillToBillModal();
                           }}
                           canDelete={true}
                           columnVisibility={columnVisibility}
                           entityType={'unusedPurchase'}
                           type="purchasein"
                        />
                     </div>
                  )}
               </div>

               {/* Mobile Add Button */}
               {isMobileOrTablet && (user?.isAdmin || purchaseInvoiceRight?.add) && (
                  <div className="fixed bottom-6 right-6 z-50">
                     <button
                        onClick={() => navigate('/admin/purchase/invoice/add')}
                        className={`p-4 rounded-full shadow-lg ${isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                           } text-white font-bold text-xl transition-all`}>
                        +
                     </button>
                  </div>
               )}
            </div>
         </div>

         {isOpen && (
            <div className="fixed inset-0 z-[999]">
               {isModalLoading ? (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                     <Loader />
                  </div>
               ) : (
                  <SalesDetails
                     isOpen={isOpen}
                     onClose={handleOnClose}
                     title="Purchase Invoice"
                     entityType="purchaseinvoice"
                     singleEntity={singleEntity}
                     setIsEditFormOpen={setIsEditFormOpen}
                     setIsOpenSingleView={setIsOpenSingleView}
                     isEditFormOpen={isEditFormOpen}
                     handlePrevPage={handlePrevPage}
                     handleNextPage={handleNextPage}
                     paginate={paginateData}
                     viewDetail={viewDetail}
                     lineItems={viewDetail?.lineitems}
                     billTerms={viewDetail?.purchasebillteams}
                     onDelete={deletePurchase}
                     handleSerchClose={() => console.log('')}
                     handleViewSingleData={handleViewSingleData}
                     singleEntityRights={purchaseInvoiceRight}
                  />
               )}
            </div>
         )}

         <SalesInvoiceFilterModal
            entityType="purchaseinvoice"
            isOpen={isFilterOpen}
            onClose={onFilterClose}
            onApplyFilters={handleApplyFilters}
            initialFilters={filters}
            Totals={Totals}
         />

         <TransactionDetailModal
            isOpen={isBillToBillModal}
            onClose={handleOnCloseBillToBill}
            accountId={accountId}
            companyId={companyId}
            invoiceData={invoiceData}
            type="PURCHASE"
            page={currentPage}
            limit={limit}
         />

         <DeletePasswordModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onConfirm={deleteRecordForSales}
            id={dataId}
            deleteApi={deleteSyncCode}
            dispatch={dispatch}
            type="purchase"
            userGlobalSettingsData={userGlobalSettingsData}
         />
      </>
   );
};

export default UnusedPurchaseInvoiceMaster;
